import axios from "axios";

const API_URL = process.env.VUE_APP_ZHULIA_SELLER_CENTER_URL;
const PRIVATE_KEY = process.env.VUE_APP_ZHULIA_PRIVATE_KEY || "PrivateKey l3Lrk4DcyGidIK8t7zKz5zF6zoL3oh1k";

class FinancingAndPaymentsService {
  headers = {
    Authorization: PRIVATE_KEY,
  };

  /**
   * Get all financing applications
   */
  async getAllFinancingApplications(params = {}) {
    try {
      const response = await axios.get(
        API_URL + `/AdminFinancing/get-all-financing-applications`,
        {
          headers: this.headers,
          params: {
            pageIndex: 1,
            pageSize: 10,
            ...params,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get eligible factored invoices for disbursement
   */
  async getEligibleFactoredInvoicesForDisbursement(params = {}) {
    try {
      const response = await axios.get(
        API_URL + `/AdminOrderManagement/get-eligible-factored-invoices-for-disbursement`,
        {
          headers: this.headers,
          params: {
            pageSize: 100, // Default page size as requested
            ...params,
          },
        }
      );
      
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Create a disbursement request
   * @param {string} sellerId - The ID of the seller
   * @param {Object} data - The disbursement data
   * @param {string} data.factoringId - The ID of the factoring record
   * @param {number} data.amount - The amount to disburse
   */
  async createDisbursement(sellerId, data) {
    try {
      const response = await axios.post(
        API_URL + `/AdminOrderManagement/create-disbursement/${sellerId}`,
        data,
        {
          headers: this.headers,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get disbursements with pagination and filters
   */
  async getDisbursements(params = {}) {
    const defaultParams = {
      pageIndex: 1,
      pageSize: 10,
      ...params,
    };
    
    try {
      const response = await axios.get(
        API_URL + `/AdminOrderManagement/get-disbursements`,
        {
          headers: this.headers,
          params: defaultParams,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get disbursement details by ID
   */
  async getDisbursementDetails(disbursementId) {
    try {
      const response = await axios.get(
        API_URL + `/AdminOrderManagement/get-disbursement/${disbursementId}`,
        {
          headers: this.headers,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get disbursement statistics
   */
  async getDisbursementStatistics() {
    try {
      const response = await axios.get(
        API_URL + `/AdminOrderManagement/get-disbursement-statistics`,
        {
          headers: this.headers,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get eligible invoices for repayment
   */
  async getEligibleInvoicesForRepayment(params = {}) {
    try {
      const response = await axios.get(
        API_URL + `/AdminOrderManagement/get-eligible-invoices-for-repayment`,
        {
          headers: this.headers,
          params: {
            pageSize: 100, // Default page size for dropdown
            ...params,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Record a repayment
   * @param {Object} data - The repayment data
   * @param {number} data.invoiceId - The ID of the invoice
   * @param {number} data.amount - The repayment amount
   * @param {string} data.paymentMethod - The payment method
   * @param {string} data.repaymentDate - The repayment date
   * @param {string} data.referenceTransactionId - The reference transaction ID
   * @param {string} data.notes - Additional notes
   * @param {Array} data.attachments - Array of attachment URLs
   * @param {string} data.recordedBy - Who recorded the repayment
   */
  async recordRepayment(data) {
    try {
      const response = await axios.post(
        API_URL + `/AdminOrderManagement/record-repayment`,
        data,
        {
          headers: this.headers,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  async getRepayments(params = {}) {
    try {
      const defaultParams = {
        pageIndex: 1,
        pageSize: 10,
        sortColumn: "createdAt",
        sortDir: "desc",
        ...params,
      };

      const response = await axios.get(
        `${API_URL}/AdminOrderManagement/get-repayments`,
        {
          headers: this.headers,
          params: defaultParams,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get payments for a specific invoice
   */
  async getPaymentsForInvoice(invoiceId) {
    try {
      const response = await axios.get(
        `${API_URL}/AdminOrderManagement/get-repayments`,
        {
          headers: this.headers,
          params: {
            invoiceId: invoiceId
          }
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  async sendPaymentReminder(invoiceId) {
    try {
      const response = await axios.post(
        `${API_URL}/AdminOrderManagement/send-payment-reminder/${invoiceId}`,
        {},
        {
          headers: this.headers,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get all seller invoices with pagination and filters
   */
  async getAllSellerInvoices(params = {}) {
    const defaultParams = {
      pageIndex: 1,
      pageSize: 10,
      sortColumn: "createdAt",
      sortDir: "desc",
      ...params,
    };
    
    try {
      const response = await axios.get(
        API_URL + `/AdminOrderManagement/get-all-seller-invoices`,
        {
          headers: this.headers,
          params: defaultParams,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get seller factored invoice statistics
   */
  async getSellerFactoredInvoiceStats() {
    try {
      const response = await axios.get(
        API_URL + `/AdminOrderManagement/get-seller-factored-invoice-stats`,
        {
          headers: this.headers,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get settlement statistics for a seller
   */
  async getSettlementStats(sellerId) {
    try {
      const response = await axios.get(
        API_URL + `/AdminOrderManagement/get-settlement-stats/${sellerId}`,
        {
          headers: this.headers,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get wallet statistics
   */
  async getWalletStats() {
    try {
      const response = await axios.get(
        API_URL + `/AdminWallet/get-all-wallet-details`,
        {
          headers: this.headers,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get collections wallet transactions
   */
  async getCollectionsWalletTransactions(params = {}) {
    try {
      const response = await axios.get(
        API_URL + `/AdminWallet/get-collections-wallet-transactions`,
        {
          headers: this.headers,
          params: {
            pageIndex: 1,
            pageSize: 10,
            ...params,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Get agent wallet transactions (disbursement history)
   */
  async getAgentWalletTransactions(params = {}) {
    try {
      const response = await axios.get(
        API_URL + `/AdminWallet/get-agent-wallet-transactions`,
        {
          headers: this.headers,
          params: {
            pageIndex: 1,
            pageSize: 10,
            ...params,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  }

}

export default new FinancingAndPaymentsService(); 